import {
  getApiErrorUserMessage,
  readApiError,
  type ApiErrorResult,
} from "_shared/_errors/api-error";
import { refreshStaleStateOnce } from "_shared/_errors/stale-state";

export type NotificationOperation =
  | "archive"
  | "unarchive"
  | "mark-seen"
  | "mark-unseen"
  | "mark-all-seen"
  | "action-link";

const operationFallbacks: Readonly<Record<NotificationOperation, string>> = {
  archive: "The notification could not be archived. Refresh the notification list and try again.",
  unarchive: "The notification could not be restored. Refresh the notification list and try again.",
  "mark-seen": "The notification could not be marked as read. Refresh the notification list and try again.",
  "mark-unseen": "The notification could not be marked as unread. Refresh the notification list and try again.",
  "mark-all-seen": "The notifications could not be marked as read. Refresh the notification list and try again.",
  "action-link": "This notification cannot be opened here. Open Communications to read it.",
};

export function readNotificationApiError(error: unknown): ApiErrorResult {
  return readApiError(unwrapNotificationsApiError(error));
}

/** Returns a stable, action-oriented message and never renders an arbitrary legacy response body. */
export function getNotificationErrorMessage(error: unknown, operation: NotificationOperation): string {
  const result = readNotificationApiError(error);

  if (result.code === "notification_already_active") {
    return "This notification is already active. Refresh the notification list and try again.";
  }
  if (result.code === "notification_change_forbidden" || result.status === 403) {
    return "You cannot change this notification. Refresh the notification list or contact an administrator if you believe this is an error.";
  }
  if (result.code === "notification_not_found" || result.status === 404) {
    return "This notification is no longer available. Refresh the notification list and try again.";
  }
  if (result.code === "notification_action_link_invalid") {
    return "This notification has an invalid action link. Open Communications to read it.";
  }
  if (result.code === "notification_action_link_unsupported") {
    return "This notification uses an action this version cannot open. Open Communications to read it.";
  }

  if (result.status === 409 || result.status === 412) {
    if (operation === "archive") {
      return "This notification is already archived. Refresh the notification list and try again.";
    }
    if (operation === "unarchive") {
      return "This notification is already active. Refresh the notification list and try again.";
    }
    if (operation === "mark-seen" || operation === "mark-all-seen") {
      return "This notification is already marked as read. Refresh the notification list and try again.";
    }
    if (operation === "mark-unseen") {
      return "This notification is already marked as unread. Refresh the notification list and try again.";
    }
    return "This notification changed. Refresh the notification list and try again.";
  }

  if (result.trusted) {
    const combined = combineProblemText(result.title, result.detail);
    if (combined) return combined;
  }

  return getApiErrorUserMessage(result) ?? operationFallbacks[operation];
}

export async function handleNotificationMutationFailure({
  error,
  operation,
  refresh,
  notify,
  refreshNotFound = true,
}: Readonly<{
  error: unknown;
  operation: NotificationOperation;
  refresh: () => void | Promise<void>;
  notify: (message: string) => void;
  refreshNotFound?: boolean;
}>): Promise<ApiErrorResult> {
  const rawError = unwrapNotificationsApiError(error);
  const result = readApiError(rawError);

  if (result.status === 404 && refreshNotFound) {
    try {
      await refresh();
    } catch {
      // Keep the original not-found response visible. A failed refresh must never replay the mutation.
    }
  } else if (result.status === 409 || result.status === 412) {
    try {
      await refreshStaleStateOnce(rawError, `notifications.${operation}`, refresh);
    } catch {
      // Keep the original conflict visible. A failed refresh must never replay the mutation.
    }
  }

  notify(getNotificationErrorMessage(error, operation));
  return result;
}

function unwrapNotificationsApiError(error: unknown): unknown {
  return error instanceof Error && "error" in error && (error as Error & { error?: unknown }).error !== undefined
    ? (error as Error & { error: unknown }).error
    : error;
}

function combineProblemText(title: string | undefined, detail: string | undefined): string | undefined {
  const parts = [title?.trim(), detail?.trim()].filter((value): value is string => Boolean(value));
  return [...new Set(parts)].join(" ") || undefined;
}
